import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SpatialOperationsShell } from '../components/spatialOperations/SpatialOperationsShell';
import { useSpatialOperationsLiveCamera } from '../hooks/useSpatialOperationsLiveCamera';
import { fetchOverviewSiteConditionPosture } from '../domain/continuousSafety/overviewSiteConditionSummary';
import {
  fetchSiteConditionState,
  type ConditionLifecycleSummary,
  type SiteConditionState,
} from '../domain/continuousSafety/siteConditionState';
import {
  buildVisualLifecycleModel,
  type VisualLifecycleDetail,
  type VisualZoneGeometry,
} from '../domain/continuousSafety/visualLifecycleModel';
import { isApiLifecycleHref } from '../domain/continuousSafety/conditionLifecycleNavigation';
import { presentCanonicalSafetyScanReport } from '../domain/safetyScanReport/canonicalSafetyScanReportPresenter';
import {
  conditionChoiceFromRows,
  presentSpatialOperationsScan,
  presentSpatialOperationsStages,
  resolveSpatialOperationsSelection,
  spatialOperationsIdentityChoices,
  type SpatialOperationsCameraChoice,
  type SpatialOperationsConditionChoice,
  type SpatialOperationsStageId,
} from '../domain/spatialOperations/spatialOperationsPresentation';
import {
  presentSpatialOperationsPolicy,
  selectPolicyForCondition,
} from '../domain/spatialOperations/spatialOperationsPolicy';
import {
  presentSpatialOperationsDashboardScanHistory,
  safetyScanHistoryWorkspacePath,
  type SpatialOperationsScanHistoryView,
} from '../domain/spatialOperations/spatialOperationsScanHistory';
import {
  buildSpatialOperationsPath,
  parseSpatialOperationsSearch,
} from '../domain/spatialOperations/spatialOperationsUrl';
import { useOverviewCameraEvidence } from '../hooks/useOverviewCameraEvidence';
import { useOverviewConditionEvidenceThumbs } from '../hooks/useOverviewConditionEvidenceThumbs';
import { useSiteLocationLookups } from '../hooks/useSiteLocationLookups';
import { useSiteTimeZone } from '../hooks/useSiteTimeZone';
import { isUsableThumbnailSrc, cameraOnlineForLiveStreamAttempt, isCameraEnabledForEvidence } from '../domain/overview/overviewCameraEvidence';
import { formatSiteDateTime } from '../domain/siteTime/siteTime';
import { zoneOverlayLabel, zoneOverlayPolygon } from '../domain/liveView/zoneOverlayGeometry';
import { api, ensureUiScope } from '../services/api';
import { policiesApi, type Policy } from '../services/policiesApi';
import { reportsApi } from '../services/reportsApi';
import { useAuth } from '../contexts/AuthContext';
import {
  getCapabilitiesFromUser,
  hasSafetyWorkflowAccess,
} from '../auth/access';
import {
  selectOverviewV2CameraForArea,
  selectOverviewV2RepresentativeAreas,
  type MonitoredAreaViewModel,
} from './OverviewV2AreaCentricView';
import type { OverviewAreaCameraPanelModel, OverviewAreaGeometry } from './OverviewAreaCameraPanel';
import {
  customerCameraLabel,
  customerZoneLabel,
  OVERVIEW_V2_UNASSIGNED_AREA_LABEL,
  overviewV2VisibleMountLabel,
  resolveOverviewV2CameraPlacement,
  type OverviewV2CameraPlacement,
} from './overviewV2Presentation';

function text(value: unknown): string {
  return String(value || '').trim();
}

function resolveSiteId(search: string): string {
  const parsed = parseSpatialOperationsSearch(search);
  return parsed.siteId || text(ensureUiScope().siteId);
}

function enrichAreas(
  areas: MonitoredAreaViewModel[],
  placements?: Record<string, OverviewV2CameraPlacement>,
): MonitoredAreaViewModel[] {
  return areas.map((area) => {
    const placement = resolveOverviewV2CameraPlacement(
      { cameraId: area.cameraId, deviceId: area.deviceId },
      placements,
    );
    return {
      ...area,
      areaId: area.areaId || text(placement?.areaId) || null,
      areaName: area.areaName || text(placement?.areaName) || null,
      mountLabel: area.mountLabel || text(placement?.mountLabel) || null,
    };
  });
}

function cameraChoices(areas: MonitoredAreaViewModel[], cameras: Array<{ camera_id?: string; name?: string }>): SpatialOperationsCameraChoice[] {
  const seen = new Set<string>();
  const choices: SpatialOperationsCameraChoice[] = [];
  areas.forEach((area) => {
    const cameraId = text(area.cameraId);
    if (!cameraId || seen.has(cameraId)) return;
    seen.add(cameraId);
    const listed = cameras.find((item) => text(item.camera_id) === cameraId);
    const areaLabel = text(area.areaName) || area.location || OVERVIEW_V2_UNASSIGNED_AREA_LABEL;
    choices.push({
      cameraId,
      deviceId: area.deviceId,
      areaId: area.areaId,
      areaLabel,
      cameraLabel: customerCameraLabel({ cameraName: listed?.name, location: area.location }),
      mountLabel: overviewV2VisibleMountLabel(area.areaName, area.mountLabel),
    });
  });
  return choices;
}

function conditionChoicesFromAreas(areas: MonitoredAreaViewModel[]): SpatialOperationsConditionChoice[] {
  const seen = new Set<string>();
  const choices: SpatialOperationsConditionChoice[] = [];
  areas.forEach((area) => {
    area.policies.forEach((policy) => {
      if (seen.has(policy.conditionId)) return;
      const choice = conditionChoiceFromRows(policy.current, policy.recurring);
      if (!choice) return;
      seen.add(choice.conditionId);
      choices.push({
        ...choice,
        cameraId: choice.cameraId || area.cameraId,
        zoneId: choice.zoneId || area.zoneId,
      });
    });
  });
  return choices;
}

function overlayFromZone(zone: VisualZoneGeometry | null, conditionId?: string | null): OverviewAreaGeometry | null {
  if (!zone) return null;
  const polygon = zoneOverlayPolygon(zone as any);
  if (!polygon || polygon.length < 3) return null;
  return {
    zoneId: text((zone as any).zone_id || (zone as any).id) || null,
    label: zoneOverlayLabel(zone as any) || text((zone as any).name) || null,
    polygon,
    deployed: true,
    conditionId: conditionId || null,
    measurementScopeKind: 'ZONE_POLYGON',
  };
}

const SpatialOperationsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, effectiveUser } = useAuth();
  const canOpenScan = hasSafetyWorkflowAccess(getCapabilitiesFromUser(effectiveUser || user));
  const parsed = useMemo(() => parseSpatialOperationsSearch(location.search), [location.search]);
  const siteId = useMemo(() => resolveSiteId(location.search), [location.search]);
  const timeZone = useSiteTimeZone(siteId || null);
  const lookups = useSiteLocationLookups(siteId, Boolean(siteId));
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [summary, setSummary] = useState<Awaited<ReturnType<typeof fetchOverviewSiteConditionPosture>>['status'] extends string ? any : never>(null);
  const [identityConditions, setIdentityConditions] = useState<SiteConditionState[] | null>(null);
  const [identityOnly, setIdentityOnly] = useState<boolean | null>(null);
  const [identityLoading, setIdentityLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<VisualLifecycleDetail | null>(null);
  const [conditionState, setConditionState] = useState<SiteConditionState | null>(null);
  const [zones, setZones] = useState<VisualZoneGeometry[]>([]);
  const [zonesResolved, setZonesResolved] = useState(false);
  const [conditionStateResolved, setConditionStateResolved] = useState(false);
  const [lifecycleResolved, setLifecycleResolved] = useState(false);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [scanLifecycle, setScanLifecycle] = useState<ReturnType<typeof presentCanonicalSafetyScanReport> | null>(null);
  const [scanHistory, setScanHistory] = useState<SpatialOperationsScanHistoryView>({
    status: 'loading',
    scans: [],
    latest: null,
    historyHref: safetyScanHistoryWorkspacePath(siteId),
  });
  const [selectedStage, setSelectedStage] = useState<SpatialOperationsStageId>('find');

  useEffect(() => {
    let active = true;
    if (!siteId) {
      setIdentityConditions(null);
      setIdentityOnly(null);
      setIdentityLoading(false);
      setSummary(null);
      setLoading(false);
      setSummaryError('Select a site to load Spatial Operations.');
      return () => {
        active = false;
      };
    }
    setIdentityLoading(true);
    setLoading(true);
    setSummaryError(null);
    void fetchSiteConditionState({ siteId, identityOnly: true }).then((result) => {
      if (!active) return;
      if (result.status === 'success' && result.data.conditions.length) {
        setIdentityConditions(result.data.conditions);
        setIdentityOnly(Boolean(result.data.identityOnly));
        setSummaryError(null);
        setIdentityLoading(false);
        setLoading(false);
        return;
      }
      setIdentityConditions(null);
      setIdentityOnly(null);
      setIdentityLoading(false);
    }).catch(() => {
      if (!active) return;
      setIdentityConditions(null);
      setIdentityOnly(null);
      setIdentityLoading(false);
    });
    return () => {
      active = false;
    };
  }, [siteId]);

  useEffect(() => {
    let active = true;
    if (!siteId || identityLoading) {
      return () => {
        active = false;
      };
    }
    if (identityConditions && identityConditions.length) {
      return () => {
        active = false;
      };
    }
    setLoading(true);
    void fetchOverviewSiteConditionPosture(siteId).then((result) => {
      if (!active) return;
      if (result.status === 'success') {
        setSummary(result.data);
        setSummaryError(null);
      } else {
        setSummary(null);
        setSummaryError(result.status === 'error' ? result.message : 'Spatial Operations could not be loaded for this site.');
      }
      setLoading(false);
    }).catch(() => {
      if (!active) return;
      setSummary(null);
      setSummaryError('Condition-state data could not be loaded for this site.');
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [siteId, identityLoading, identityConditions]);

  const areas = useMemo(
    () => enrichAreas(selectOverviewV2RepresentativeAreas(summary), lookups.cameraPlacementById),
    [summary, lookups.cameraPlacementById],
  );

  const identityRequiredCameras = useMemo(() => {
    const seen = new Set<string>();
    return (identityConditions || []).flatMap((condition) => {
      const cameraId = text(condition.canonicalScope?.cameraIds?.[0] || condition.operationalScope?.cameraId);
      if (!cameraId || seen.has(cameraId)) return [];
      seen.add(cameraId);
      return [{
        cameraId,
        deviceId: text(condition.canonicalScope?.deviceIds?.[0] || condition.operationalScope?.deviceId) || null,
      }];
    });
  }, [identityConditions]);

  const cameraEvidence = useOverviewCameraEvidence({
    siteId,
    linkedByCamera: {
      ...areas.reduce<Record<string, number>>((acc, area) => {
        if (area.cameraId) acc[area.cameraId] = 1;
        return acc;
      }, {}),
      ...Object.fromEntries(identityRequiredCameras.map((camera) => [camera.cameraId, 1])),
    },
    requiredCameras: identityRequiredCameras.length
      ? identityRequiredCameras
      : areas.flatMap((area) => (area.cameraId ? [{ cameraId: area.cameraId, deviceId: area.deviceId }] : [])),
    enabled: Boolean(siteId),
  });

  const identityChoices = useMemo(
    () => spatialOperationsIdentityChoices({
      conditions: identityConditions || [],
      cameras: cameraEvidence.cameras || [],
      placements: lookups.cameraPlacementById,
    }),
    [identityConditions, cameraEvidence.cameras, lookups.cameraPlacementById],
  );

  const cameras = useMemo(() => {
    if (identityChoices.cameras.length) return identityChoices.cameras;
    return cameraChoices(areas, cameraEvidence.cameras || []);
  }, [identityChoices.cameras, areas, cameraEvidence.cameras]);
  const conditions = useMemo(() => {
    if (identityChoices.conditions.length) return identityChoices.conditions;
    return conditionChoicesFromAreas(areas);
  }, [identityChoices.conditions, areas]);
  const selection = useMemo(
    () => resolveSpatialOperationsSelection({
      cameras,
      conditions,
      requestedCameraId: parsed.cameraId,
      requestedConditionId: parsed.conditionId,
      requestedMode: parsed.mode,
    }),
    [cameras, conditions, parsed.cameraId, parsed.conditionId, parsed.mode],
  );

  useEffect(() => {
    if (!siteId) return;
    const nextPath = buildSpatialOperationsPath({
      siteId,
      cameraId: selection.camera?.cameraId || null,
      conditionId: selection.condition?.conditionId || null,
      mode: selection.mode,
      currentSearch: location.search,
    });
    const currentPath = `${location.pathname}${location.search || ''}`;
    if (nextPath !== currentPath && (selection.camera || selection.condition)) {
      navigate(nextPath, { replace: true });
    }
  }, [siteId, selection.camera, selection.condition, selection.mode, location.search, location.pathname, navigate]);

  const selectedArea = useMemo(
    () => areas.find((area) => area.cameraId && area.cameraId === selection.camera?.cameraId) || null,
    [areas, selection.camera?.cameraId],
  );
  const selectedPolicy = useMemo(() => {
    if (!selectedArea || !selection.condition) return null;
    return selectedArea.policies.find((item) => item.conditionId === selection.condition?.conditionId) || selectedArea.policies[0] || null;
  }, [selectedArea, selection.condition]);

  const evidenceThumbs = useOverviewConditionEvidenceThumbs({
    siteId,
    requests: selection.condition ? [{
      conditionId: selection.condition.conditionId,
      zoneId: selection.condition.zoneId,
      cameraId: selection.camera?.cameraId || null,
    }] : [],
    enabled: Boolean(siteId && selection.condition),
  });
  const recorded = selection.condition ? evidenceThumbs.byConditionId[selection.condition.conditionId] : null;

  useEffect(() => {
    const conditionId = selection.condition?.conditionId;
    if (!siteId || !conditionId) {
      setConditionState(null);
      setDetail(null);
      setConditionStateResolved(false);
      setLifecycleResolved(false);
      return;
    }
    let cancelled = false;
    setConditionStateResolved(false);
    setLifecycleResolved(false);
    const listedIdentity = (identityConditions || []).find((item) => item.conditionId === conditionId) || null;
    setConditionState(listedIdentity && identityOnly === false ? listedIdentity : null);
    void fetchSiteConditionState({ siteId, conditionIds: [conditionId] }).then((result) => {
      if (cancelled) return;
      setConditionState(result.status === 'success' ? (result.data.conditions[0] || listedIdentity || null) : listedIdentity);
      setConditionStateResolved(true);
    }).catch(() => {
      if (!cancelled) setConditionStateResolved(true);
    });
    void api.get(`/api/v1/sites/${encodeURIComponent(siteId)}/condition-lifecycle?condition_id=${encodeURIComponent(conditionId)}`)
      .then((response) => {
        if (cancelled) return;
        const payload = (response.data || {}) as { conditions?: VisualLifecycleDetail[] };
        setDetail(payload.conditions?.[0] || null);
        setLifecycleResolved(true);
      })
      .catch(() => {
        if (!cancelled) {
          setDetail(null);
          setLifecycleResolved(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, selection.condition?.conditionId, identityConditions, identityOnly]);

  useEffect(() => {
    if (!siteId) {
      setZones([]);
      setZonesResolved(false);
      return;
    }
    let cancelled = false;
    setZonesResolved(false);
    const cameraId = selection.camera?.cameraId;
    void api.get('/api/v1/zones', {
      params: cameraId ? { camera_id: cameraId } : {},
    }).then((response) => {
      if (cancelled) return;
      const rows = Array.isArray(response.data)
        ? response.data
        : (response.data?.zones || response.data?.items || []);
      setZones(Array.isArray(rows) ? rows : []);
      setZonesResolved(true);
    }).catch(() => {
      if (!cancelled) {
        setZones([]);
        setZonesResolved(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [siteId, selection.camera?.cameraId]);

  useEffect(() => {
    if (!siteId) {
      setPolicies([]);
      return;
    }
    let cancelled = false;
    void policiesApi.listPolicies(siteId, true, 'warehouse').then((result) => {
      if (!cancelled) setPolicies(result.policies || []);
    }).catch(() => {
      if (!cancelled) setPolicies([]);
    });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  const lifecycle: ConditionLifecycleSummary | null = conditionState?.conditionLifecycle
    || selectedPolicy?.recurring?.lifecycle
    || null;

  useEffect(() => {
    const reportId = text(lifecycle?.latestScan?.afterReportId);
    if (!reportId) {
      setScanLifecycle(null);
      return;
    }
    let cancelled = false;
    void Promise.all([
      reportsApi.getScanVerification(reportId).catch(() => null),
      reportsApi.openSafetyReport(reportId).catch(() => null),
    ]).then(([verification, opened]) => {
      if (cancelled) return;
      const persisted = opened && typeof opened === 'object'
        ? ((opened as { report?: Record<string, unknown> }).report || null)
        : null;
      setScanLifecycle(presentCanonicalSafetyScanReport({
        displayedReportId: reportId,
        persistedReport: persisted,
        verification: verification || null,
      }));
    }).catch(() => {
      if (!cancelled) setScanLifecycle(null);
    });
    return () => {
      cancelled = true;
    };
  }, [lifecycle?.latestScan?.afterReportId]);

  useEffect(() => {
    const conditionId = selection.condition?.conditionId;
    const historyHref = safetyScanHistoryWorkspacePath(siteId);
    if (!siteId || !conditionId) {
      setScanHistory({ status: 'ready', scans: [], latest: null, historyHref });
      return;
    }
    let cancelled = false;
    setScanHistory({ status: 'loading', scans: [], latest: null, historyHref });
    void reportsApi.getSafetyDashboard({
      org_id: text(ensureUiScope().orgId),
      site_id: siteId,
      limit: 100,
    }).then((dashboard) => {
      if (cancelled) return;
      setScanHistory(presentSpatialOperationsDashboardScanHistory({ dashboard, conditionId, siteId }));
    }).catch(() => {
      if (!cancelled) setScanHistory({ status: 'error', scans: [], latest: null, historyHref });
    });
    return () => {
      cancelled = true;
    };
  }, [siteId, selection.condition?.conditionId]);

  const visualModel = useMemo(() => {
    if (!lifecycle || !selection.condition) return null;
    return buildVisualLifecycleModel({
      lifecycle,
      condition: conditionState,
      detail,
      route: {
        siteId,
        conditionId: selection.condition.conditionId,
        areaId: selection.camera?.areaId,
        zoneId: selection.condition.zoneId,
        cameraId: selection.camera?.cameraId || null,
        conditionFamily: selection.condition.family,
      },
      zones,
      evidence: recorded || { status: 'idle', imageUrl: null, incidentId: null },
    });
  }, [lifecycle, conditionState, detail, siteId, selection.condition, selection.camera?.areaId, selection.camera?.cameraId, zones, recorded]);

  const listedCamera = selectedArea
    ? selectOverviewV2CameraForArea(cameraEvidence.cameras || [], selectedArea)
    : (cameraEvidence.cameras || []).find((camera) => text(camera.camera_id) === selection.camera?.cameraId) || null;
  const cameraEnabled = listedCamera ? isCameraEnabledForEvidence(listedCamera) : true;
  const cameraOnline = cameraOnlineForLiveStreamAttempt(listedCamera);
  const snapshotUrl = (selectedArea?.cameraId || selection.camera?.cameraId)
    && isUsableThumbnailSrc(cameraEvidence.thumbnails[selectedArea?.cameraId || selection.camera?.cameraId || ''])
    ? cameraEvidence.thumbnails[selectedArea?.cameraId || selection.camera?.cameraId || '']
    : null;
  const liveCamera = useSpatialOperationsLiveCamera({
    active: selection.mode === 'camera',
    cameraId: selection.camera?.cameraId,
    edgeDeviceId: selection.camera?.deviceId || listedCamera?.edge_device_id,
    cameraOnline,
    cameraEnabled,
    snapshotUrl,
  });
  const selectedZone = zones.find((zone) => {
    const zoneId = text((zone as any).zone_id || (zone as any).id);
    return zoneId && zoneId === (selection.condition?.zoneId || selectedArea?.zoneId);
  }) || null;
  const overlay = overlayFromZone(selectedZone, selection.condition?.conditionId);
  const panelCameraId = selectedArea?.cameraId || selection.camera?.cameraId || null;
  const panelDeviceId = selectedArea?.deviceId || selection.camera?.deviceId || listedCamera?.edge_device_id || null;
  const cameraPanel: OverviewAreaCameraPanelModel | null = selection.camera ? {
    areaTitle: selectedArea?.title || selection.camera.areaLabel,
    cameraLabel: selection.camera.cameraLabel,
    cameraId: panelCameraId,
    deviceId: panelDeviceId,
    zoneLabel: customerZoneLabel({
      zoneName: overlay?.label || selectedArea?.zoneId || selection.condition?.zoneId,
      location: selectedArea?.location || selection.camera.areaLabel,
    }),
    zoneId: selectedArea?.zoneId || selection.condition?.zoneId || null,
    snapshotUrl,
    snapshotPending: cameraEvidence.thumbnailsLoading && !snapshotUrl,
    geometry: overlay,
    overlays: overlay ? [overlay] : [],
    cameraPath: panelCameraId
      ? `/cameras/${encodeURIComponent(panelCameraId)}${siteId ? `?site=${encodeURIComponent(siteId)}` : ''}`
      : null,
    livePath: (panelCameraId || panelDeviceId)
      ? `/live-view?${new URLSearchParams({
        ...(siteId ? { site: siteId } : {}),
        ...(panelCameraId ? { camera_id: panelCameraId } : {}),
        ...(panelDeviceId ? { device_id: panelDeviceId } : {}),
      }).toString()}`
      : null,
    zonePath: (selectedArea?.zoneId || selection.condition?.zoneId)
      ? `/zones?${new URLSearchParams({
        ...(siteId ? { site_id: siteId } : {}),
        zone_id: selectedArea?.zoneId || selection.condition?.zoneId || '',
        ...(panelCameraId ? { camera_id: panelCameraId } : {}),
      }).toString()}`
      : null,
    areaName: selectedArea?.areaName || selection.camera.areaLabel,
    mountLabel: overviewV2VisibleMountLabel(
      selectedArea?.areaName || selection.camera.areaLabel,
      selectedArea?.mountLabel || selection.camera.mountLabel,
    ),
  } : null;

  const timeLabel = (value: string | null | undefined) => {
    if (!value) return null;
    return formatSiteDateTime(value, timeZone) || value;
  };
  const stages = presentSpatialOperationsStages({
    conditionLabel: selection.condition?.label || 'Condition',
    areaLabel: selection.camera?.areaLabel || 'Area',
    zoneLabel: cameraPanel?.zoneLabel,
    current: selectedPolicy?.current,
    recurring: selectedPolicy?.recurring,
    recurrenceRange: visualModel?.timeSemantics.recurrenceRange,
    linkedOccurrenceCount: visualModel?.lifecycleOccurrenceCount ?? null,
    lifecycle,
    scanLifecycle: scanLifecycle?.lifecycle || null,
    historicalScanCount: scanHistory.status === 'ready' ? scanHistory.scans.length : null,
    siteId,
    conditionId: selection.condition?.conditionId || '',
    areaId: selection.camera?.areaId,
    zoneId: selection.condition?.zoneId,
    cameraId: selection.camera?.cameraId,
    conditionFamily: selection.condition?.family,
    canOpenScan,
    timeLabel,
  });
  const presentedScan = presentSpatialOperationsScan({
    lifecycle,
    scanLifecycle: scanLifecycle?.lifecycle || null,
    confirmedLabel: scanLifecycle?.confirmedLabel,
    usableHoursLabel: scanLifecycle?.usableHoursLabel,
    siteId,
    conditionId: selection.condition?.conditionId || '',
    areaId: selection.camera?.areaId,
    zoneId: selection.condition?.zoneId,
    cameraId: selection.camera?.cameraId,
    canOpenScan,
  });
  const scan = !presentedScan.linked
    && scanHistory.status === 'ready'
    && scanHistory.scans.length > 0
    ? { ...presentedScan, emptyCopy: 'No Control-linked verification Scan is recorded yet.' }
    : presentedScan;
  const policyView = presentSpatialOperationsPolicy({
    policy: selectPolicyForCondition({
      policies,
      conditionFamily: selection.condition?.family,
      cameraId: selection.camera?.cameraId,
      zoneId: selection.condition?.zoneId,
    }),
    conditionLabel: selection.condition?.label || 'Condition',
  });

  return (
    <SpatialOperationsShell
      binding="live"
      siteId={siteId}
      siteLabel={lookups.siteDisplayName || null}
      loading={loading}
      error={summaryError}
      cameras={cameras}
      conditions={conditions}
      selectedCamera={selection.camera}
      selectedCondition={selection.condition}
      mode={selection.mode}
      cameraPanel={cameraPanel}
      liveCamera={liveCamera}
      cameraOnline={cameraOnline}
      cameraFreshness={cameraOnline === false ? 'Camera offline' : null}
      visualModel={visualModel}
      stages={stages}
      selectedStage={selectedStage}
      scan={scan}
      scanHistory={scanHistory}
      policy={policyView}
      recordedEvidenceUrl={recorded?.status === 'ready' ? recorded.imageUrl : null}
      recordedEvidenceHref={visualModel?.evidenceHref || null}
      timeZone={timeZone}
      enrichmentPending={(!conditionStateResolved || !lifecycleResolved) && Boolean(selection.condition)}
      geometryPending={!zonesResolved}
      onNavigate={(path) => {
        if (isApiLifecycleHref(path)) return;
        navigate(path);
      }}
      onSelectCamera={(cameraId) => {
        navigate(buildSpatialOperationsPath({
          siteId,
          cameraId,
          conditionId: null,
          mode: selection.mode,
          currentSearch: location.search,
        }));
      }}
      onSelectCondition={(conditionId) => {
        navigate(buildSpatialOperationsPath({
          siteId,
          cameraId: selection.camera?.cameraId || null,
          conditionId,
          mode: selection.mode,
          currentSearch: location.search,
        }));
      }}
      onSelectMode={(mode) => {
        navigate(buildSpatialOperationsPath({
          siteId,
          cameraId: selection.camera?.cameraId || null,
          conditionId: selection.condition?.conditionId || null,
          mode,
          currentSearch: location.search,
        }));
      }}
      onSelectStage={setSelectedStage}
    />
  );
};

export default SpatialOperationsPage;
